import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

import { useConfig } from '@/composables/config'
import { PLAYGAMA_CLIDS } from '@/constants/general'
import type { AvailableLocale } from '@/i18n/helpers'
import { useI18nAddon } from '@/i18n/i18n'
import { type AnalyticsClient, Analytics } from '@/modules/analytics'
import { MockAnalytics } from '@/modules/analytics/mock-analytics'
import { type RawPlaygamaCTREvent, type RawPlaygamaEvent, type RawRumMetricData } from '@/modules/analytics/types'
import { getDevtools } from '@/modules/devtools'
import { ROUTES_WITH_GAME_PARAM } from '@/router/constants'
import { useAdBlockStore } from '@/store/ad-block-store'
import { useAppStore } from '@/store/app-store'
import { useClientAppStore } from '@/store/client-app-store'
import { useUserStore } from '@/store/user-store/user-store'
import type { PlatformType } from '@/types'
import {
    ADJUST_LOG_TOKENS,
    type AvailableEvent,
    type AvailablePGEvents,
    type CustomEventForGa,
    type CustomPGEvents,
    type EventForGa,
    type EventGA,
    type LoggerEvent,
    type LogEventOptions,
    type LogSource,
} from '@/types/logger.types'
import { getDspClickIdForSession } from '@/utils/dsp-click-attribution'
import { idleCallbackPromise, random, timer } from '@/utils/helpers'

import { useNativeAppStore } from '../native-app-store'

const INITIAL_DELAY = 5000 // ms
let initialDelayPromise: null | Promise<void> = null

function initialDelay() {
    if (!initialDelayPromise) {
        initialDelayPromise = timer(INITIAL_DELAY)
    }
    return initialDelayPromise
}

const dataLayerQueue: Record<string, unknown>[] = []
let isDrainingDataLayer = false

// gtag processes every push synchronously (~50 ms on a phone), and idle callbacks queued together
// run back to back in one idle period, so the pushes are issued one per idle period instead
async function pushDataLayer(data: Record<string, unknown>) {
    dataLayerQueue.push(data)
    if (isDrainingDataLayer) {
        return
    }

    isDrainingDataLayer = true
    try {
        await initialDelay()
        while (dataLayerQueue.length) {
            await idleCallbackPromise()
            window.dataLayer.push(...dataLayerQueue.splice(0, 1))
        }
    } finally {
        // a push that throws (e.g. a blocker's stub) must not leave the queue stuck behind the flag
        isDrainingDataLayer = false
    }
}

type EventData = {
    source: LogSource
    pageName: string | undefined
    gameHru: string | undefined
    clid: string | undefined
    userId: string | undefined
    sessionId: string
    widgetId: string | undefined
    rankingClusterId: string | undefined
    rankingAudience: string | undefined
    experiments?: string
    locale: AvailableLocale
    userAgent: string | undefined
    referer: string | undefined
    userAcquisitionParams: Record<string, string> | undefined
    pgDspClickId?: string
    platform: PlatformType
    contentScope: string | undefined
    adBlockStatus: 'enabled' | 'disabled' | 'unknown'
}

type PortalEventData = Omit<EventData, 'userId' | 'sessionId'> & {
    userId?: string
    sessionId?: string
}

const GTM_EVENTS: AvailablePGEvents[] = [
    'homepage_5s',
    'game',
    'game_15s',
    'game_30s',
    'game_60s',
    'game_preview',
    'play',
    'playgame_10min',
    'playgame_3min',
    'playgame_5min',
    'timespent_ltv',
    'purchase_successful',
    'preroll_end',
    'login',
]
const checkIsEventGTM = (eventName: AvailablePGEvents): eventName is AvailableEvent =>
    GTM_EVENTS.includes(eventName as CustomPGEvents)
const mockAnalytics = new MockAnalytics()

export const useLoggerStore = defineStore('logger', () => {
    const { ANALYTICS_API_HOST } = useConfig()
    const nativeAppSDK = useNativeAppStore()
    const route = useRoute()
    const userStore = useUserStore()
    const appStore = useAppStore()
    const clientAppStore = useClientAppStore()
    const adBlockStore = useAdBlockStore()
    const routePageName = computed(() => (route.name as string) || undefined)
    const { currentLocale } = useI18nAddon()
    const ctrTestGroup = ref<boolean>()
    const { VUE_APP_CTR_SAMPLING_VALUE } = useConfig()
    let pgAnalyticsInstance: AnalyticsClient | null = null

    function getPgAnalytics(): AnalyticsClient {
        if (!appStore.isLoggingEnabled) {
            return mockAnalytics
        }
        if (import.meta.env.SSR) {
            throw new Error('Analytics can only be used in browser context')
        }
        if (!pgAnalyticsInstance) {
            pgAnalyticsInstance = new Analytics(`${ANALYTICS_API_HOST}/api/events/v2`)
        }
        return pgAnalyticsInstance
    }

    function getEventData(sessionId?: string): Omit<EventData, 'userId' | 'sessionId'> {
        const userAcquisitionParams = {
            ...clientAppStore.userAcquisitionParams,
        }

        return {
            source: 'default',
            clid: appStore.clid,
            widgetId: clientAppStore.widgetId,
            rankingClusterId: appStore.rawRankingParams?.clusterId,
            rankingAudience: appStore.rawRankingParams?.audience,
            pageName: routePageName.value,
            gameHru: ROUTES_WITH_GAME_PARAM.includes(routePageName.value || '')
                ? (route.params.game as string | undefined)
                : undefined,
            experiments: clientAppStore.experimentsString,
            locale: currentLocale.value,
            referer: clientAppStore.referer,
            userAgent: clientAppStore.userAgent,
            userAcquisitionParams,
            pgDspClickId: sessionId ? getDspClickIdForSession(sessionId) : undefined,
            platform: appStore.platform,
            contentScope: appStore.contentScope,
            adBlockStatus: adBlockStore.adBlockStatus,
        }
    }

    async function getUserEventData(): Promise<EventData> {
        const sessionId = clientAppStore.getSessionId()
        const user = await userStore.getUser()
        const restData = getEventData(sessionId)
        return {
            ...restData,
            userId: user?._id,
            sessionId,
        }
    }

    function getSyncUserEventData(): EventData {
        const sessionId = clientAppStore.getSessionId()
        return {
            ...getEventData(sessionId),
            userId: userStore.user?._id,
            sessionId,
        }
    }

    function createPortalEvent(event: LoggerEvent, eventData: PortalEventData): RawPlaygamaEvent {
        const {
            source,
            clid,
            userId,
            sessionId,
            widgetId,
            rankingClusterId,
            rankingAudience,
            pageName,
            gameHru,
            experiments,
            locale,
            referer,
            userAgent,
            platform,
            userAcquisitionParams,
            pgDspClickId,
            contentScope,
            adBlockStatus,
        } = eventData

        const { waterfallIndex, ...rest } = event
        return {
            ...rest,
            source,
            pageName,
            gameHru,
            userId,
            sessionId,
            clid,
            widgetId,
            rankingClusterId,
            rankingAudience,
            referer,
            locale,
            userAgent,
            platform,
            waterfallIndex: waterfallIndex?.toString(),
            experiments,
            utm: userAcquisitionParams,
            pgDspClickId,
            contentScope,
            adBlockStatus,
        }
    }

    async function logEvent(event: LoggerEvent, options: LogEventOptions = {}): Promise<void> {
        getDevtools()?.record('analytics', event.eventName, event)
        const isImmediate = options.flushImmediately === true
        if (!isImmediate) {
            await idleCallbackPromise()
        }

        const eventData = isImmediate ? getSyncUserEventData() : await getUserEventData()

        if (checkIsEventGTM(event.eventName)) {
            const gaEvent: EventForGa = {
                event: event.event as EventGA,
                eventName: event.eventName,
                action: event.action || '',
                clid: eventData.clid,
                experiments: eventData.experiments,
                widget_id: eventData.widgetId || '',
                game_provider: '',
                game_categories: [],
                ad_placement: event.adPlacement || '',
                ad_unit: event.adUnit || '',
                instream_duration: event.instreamDuration ?? null,
                watched_duration: event.watchedDuration ?? null,
                waterfall_index: event.waterfallIndex ?? null,
                error_message: event.errorMessage || '',
                amount_in_game_currency: event.amountInGameCurrency ?? null,
            }
            pushDataLayer(gaEvent)
            sendToNativeApp(gaEvent)
        }

        const analytics = getPgAnalytics()
        const portalEvent = createPortalEvent(event, eventData)
        isImmediate ? analytics.logAndFlush(portalEvent) : analytics.log(portalEvent)

        const adjustToken = ADJUST_LOG_TOKENS[event.eventName as AvailablePGEvents]
        if (adjustToken) {
            sendToAdjust(event.eventName, adjustToken)
        }
    }

    async function logInterfaceEvents(props: {
        interface_target?: string
        interface_path?: string
        eventName: AvailableEvent
    }) {
        getDevtools()?.record('analytics', props.eventName, props)
        const { clid, userId, widgetId, pageName, gameHru, locale, referer, userAgent, platform } =
            await getUserEventData()

        getPgAnalytics().logUiEvent({
            eventName: props.eventName,
            userTimestamp: new Date().toISOString(),
            pageName,
            gameHru,
            userId,
            clid,
            widgetId: widgetId || '',
            referer,
            locale,
            userAgent,
            platform,
            interfacePath: props.interface_path || 'unknown',
            interfaceTarget: props.interface_target || 'unknown',
        })
    }

    async function logCTREvents(props: Omit<RawPlaygamaCTREvent, 'sample_rate'>) {
        const eventData = await getUserEventData()
        if (eventData.clid && Object.values(PLAYGAMA_CLIDS).includes(eventData.clid) && ctrTestGroup.value) {
            getPgAnalytics().logCTREvent({
                ...eventData,
                ...props,
                sample_rate: VUE_APP_CTR_SAMPLING_VALUE,
            })
        }
    }

    function startRumEvent(metric: Omit<RawRumMetricData, 'experiments' | 'platform'>) {
        const { experiments, platform, clid, pageName, adBlockStatus } = getEventData()
        return getPgAnalytics().startRumEvent({
            ...metric,
            platform,
            experiments,
            clid,
            pageName,
            adBlockStatus,
        })
    }

    function setCTRSampling() {
        ctrTestGroup.value = random() < VUE_APP_CTR_SAMPLING_VALUE
    }

    function sendToNativeApp(event: CustomEventForGa) {
        const supportsSpread = appStore.isVersionAtLeast({ ios: '1.0.5', android: '1.0.29' })

        if (supportsSpread) {
            nativeAppSDK.postMessage('analytics.event', { name: event.eventName, ...event })
        } else {
            nativeAppSDK.postMessage('analytics.event', { name: event.eventName, payload: event })
        }
    }

    function sendToAdjust(eventName: AvailablePGEvents, eventToken: string) {
        const { gameHru } = getEventData()
        nativeAppSDK.postMessage('adjust.event', { eventName, eventToken, ...(gameHru ? { game_hru: gameHru } : {}) })
    }

    function registerPageHideFallbackEvent(event: LoggerEvent): () => void {
        const sessionId = clientAppStore.getSessionId()
        return getPgAnalytics().registerPageHideFallbackEvent(
            createPortalEvent(event, { ...getEventData(sessionId), sessionId }),
        )
    }

    return {
        logEvent,
        logInterfaceEvents,
        startRumEvent,
        registerPageHideFallbackEvent,
        setCTRSampling,
        logCTREvents,
    }
})
